import { isMembership } from "@app-contexts/dashboard/features/box-office/sell/components/Inventory/helpers";
import { common, enums } from "@shared/constants";
import { DataProvider } from "@shared/constants/common";
import { useMultiSeatSelect } from "@shared/hooks/seating/useMultiSeatSelect/useMultiSeatSelect";
import { useAwaitComponent } from "@shared/hooks/useAwaitComponent/useAwaitComponent";
import { useBasketService } from "@shared/modules/basket/context/BasketServiceContext";
import { TicketItemGroupPermissionTypeSeat } from "@shared/modules/basket/types/common/ticket-item-group/ticket-item-group-type";
import { checkItemGroupsHavePermissions } from "@shared/modules/basket/utils/helpers";
import {
  TicketItemGroupPermissionType,
  TicketItemGroupType,
} from "@shared/modules/item-groups/types/ticket-item-group-type";
import { useStore } from "@shared/store/useStore";
import { getTotalWaitlistedSeatQty } from "@shared/utils/seating/seating-waitlist-utils";
import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { SeatingModal } from "../enums/seating-enums";
import useBestAvailableQueryParams, {
  BestAvailableQueryActions,
} from "../features/BestAvailable/hooks/useBestAvailableQueryParams";
import SeatingService from "../services/SeatingService";
import StageService from "../services/StageService";
import {
  SeatingEventPropertyMap,
  SeatingItemPropertyMap,
} from "../types/seating-property-types";
import {
  BestAvailableSeatingQueryParamsActionType,
  BestAvailableSeatingQueryParamsType,
  CartPermissionType,
  FormattedSeatType,
  LocationDataType,
  SeatDataType,
  SeatSelectionModalReturnType,
  SeatsToAddType,
  SeatsToMultiSelectType,
  SeatWithLocationDataType,
} from "../types/seating-types";

const SeatingStateContext = createContext<SeatingStateContextType | undefined>(
  undefined
);

export type SeatingStateContextType = {
  basketType: DataProvider;
  seatingServiceState: SeatingService;
  stageServiceState: StageService;
  enableTracking: boolean;
  isUpdatingMap: boolean;
  isLoadingMap: boolean;
  isOnSeatingMapPage: boolean;
  isModalOutsideMap: boolean;
  countOfSelectedItems: number;
  paymentType: number | undefined;
  purchaseSourcePlatform: string | undefined;
  dynamicHoldSlug: string | undefined;
  packageItemGroupParent: number | null;
  seatingItemArray: Array<SeatingItemPropertyMap> | undefined;
  seatingEventData: SeatingEventPropertyMap | undefined;
  selectedSeatIds: number[];
  seatingObjectArray: Array<SeatWithLocationDataType>;
  updateSeatingObjectArray: () => void;
  requiresBasket: boolean;
  updateSeatingEventData: (val: SeatingEventPropertyMap) => void;
  updateSeatingItemArray: (val: Array<SeatingItemPropertyMap>) => void;
  updateItemGroupParent: (input: number) => void;
  updateSeatingState: () => void;
  synchronizeSeating: () => void;
  resetSeating: () => void;
  updateRequiresBasket: (input: boolean) => void;
  updateDynamicHoldSlug: (input: string) => void;
  setIsUpdatingMap: React.Dispatch<React.SetStateAction<boolean>>;
  setIsLoadingMap: React.Dispatch<React.SetStateAction<boolean>>;
  setDynamicHoldSlug: React.Dispatch<React.SetStateAction<string | undefined>>;
  setBasketHoldID: (id: number | undefined) => void;
  getInfoRequiredLocationQtyInCart: (
    itemId: number,
    permissionId: number,
    fallbackNumber: number
  ) => number;
  getLocationQtyInCart: (
    itemId: number,
    permissionId: number,
    fallbackNumber: number
  ) => number;
  resetMapSelections: () => void;
  updateBestAvailableSeatingQueryParams: React.Dispatch<BestAvailableSeatingQueryParamsActionType>;
  bestAvailableSeatingQueryParams: BestAvailableSeatingQueryParamsType;
  disableBestAvailableSeating: boolean;
  hasSeatsWithMultipleTypes: boolean;
  hasPasswordProtectedSeats: boolean;
  canShowBestAvailableSeating: boolean;
  typesOnMap: number[];
  isSeatingBasketUpdating: boolean;
  updateSeatingBasketUpdating: (input: boolean) => void;
  actions: {
    onMultiSeatSelect(
      seats: Array<SeatDataType>
    ): Promise<null | Array<SeatDataType>>;
    handleRemoveIsolatedSeats(
      originalSeat: FormattedSeatType,
      isolatedSeats: Array<number>
    ): Promise<SeatSelectionModalReturnType | null>;
    handleAddIsolatedSeats(
      originalSeat: FormattedSeatType,
      isolatedSeats: Array<number>
    ): Promise<SeatSelectionModalReturnType>;
    handleAddFullPurchaseSeats(
      $data: SeatWithLocationDataType
    ): Promise<SeatSelectionModalReturnType>;
    handleRemoveFullPurchaseSeats(
      $data: SeatWithLocationDataType
    ): Promise<SeatSelectionModalReturnType>;
    removePendingCartPermissions: (seats: Array<FormattedSeatType>) => void;
    getResaleSeatSelections({
      originalSeat,
      resaleId,
      remove,
    }: {
      originalSeat: FormattedSeatType;
      resaleId: number;
      remove?: boolean;
    }): Promise<SeatSelectionModalReturnType | null>;
    getMinPurchaseSeatSelections({
      seat,
      inCartPermission,
      minPurchaseLimit,
      itemGroupParent,
    }: {
      seat: FormattedSeatType;
      inCartPermission: CartPermissionType;
      minPurchaseLimit?: number;
      itemGroupParent?: number | undefined;
    }): Promise<SeatSelectionModalReturnType | null>;
    getIsolatedSeatSelections({
      originalSeat,
      remove,
      seatDataWithLocation,
    }: {
      originalSeat: FormattedSeatType;
      remove: boolean;
      seatDataWithLocation?: SeatWithLocationDataType;
    }): Promise<SeatSelectionModalReturnType | null>;
    initRemoveSeatPermissions: (
      permission: TicketItemGroupPermissionType,
      itemGroup: TicketItemGroupType,
      eventData: SeatingEventPropertyMap
    ) => Promise<void | null>;
    initModifyLocationPermissions(
      permission: TicketItemGroupPermissionType,
      itemGroup: TicketItemGroupType,
      quantity: number
    ): Promise<void>;
    getFullPurchaseRequiredConfirmation: (
      $data: SeatWithLocationDataType,
      remove?: boolean
    ) => Promise<SeatSelectionModalReturnType | null>;
    removeSeatPermission: (
      permissionId: number,
      itemGroupParentId: number | null,
      eventData?: SeatingEventPropertyMap
    ) => Promise<void | null>;
    requestRemoveSeatPermissions(
      seats: Array<FormattedSeatType>
    ): Promise<void>;
    requestAddSeatPermissions(seats: Array<FormattedSeatType>): Promise<void>;
    requestModifySeatPermissions(
      seats: Array<FormattedSeatType>,
      itemGroupId?: number
    ): Promise<void>;
    requestUpdateLocationPermissions(
      locationArray: Array<{
        itemTypeId: number;
        permissionId: number;
        qty: number;
      }>,
      location?: LocationDataType,
      itemGroupId?: number | undefined
    ): Promise<void>;
    requestModifyLocationPermissions(
      locationArray: Array<{
        itemTypeId: number;
        permissionId: number;
        qty: number;
      }>,
      location?: LocationDataType,
      itemGroupParent?: number | undefined,
      isWaitlist?: boolean
    ): Promise<void>;
    requestAddSeatsAndLocationsToCart({
      seats,
      locations,
      itemGroupParent,
      isWaitlist = false,
    }: {
      seats?: Array<FormattedSeatType>;
      locations?: Array<{
        itemTypeId: number;
        permissionId: number;
        qty: number;
        location?: {
          id: number;
          identifier: string;
          section?: string;
          count?: number;
        };
        payWhatYouCanPrice?: string;
      }>;
      itemGroupParent?: number;
      isWaitlist?: boolean;
    }): Promise<void>;
  };
  modalValues: {
    seatsToAdd:
      | undefined
      | {
          originalSeat: SeatWithLocationDataType;
          originalPendingItem?: number;
          seatIdArray: Array<number>;
          modalType: Omit<SeatingModal, "minpurchase">;
        };
    seatsToRemove:
      | undefined
      | {
          originalSeat: SeatWithLocationDataType;
          seatIdArray: Array<number>;
          formattedSeatArray?: Array<FormattedSeatType>;
          modalType: SeatingModal;
        };
    removeAllSeatsData:
      | undefined
      | {
          $data: TicketItemGroupPermissionTypeSeat;
          eventData: SeatingEventPropertyMap;
        };
    seatsToMultiSelect: undefined | SeatsToMultiSelectType;
    modalOpen: boolean;
    awaitModal(): Promise<SeatSelectionModalReturnType>;
    resolveModal: (val: unknown) => void;
    rejectModal: (err: unknown) => void;
  };
};

type SeatingStateProviderProps = {
  basketType: common.DataProvider;
  isOnSeatingMapPage: boolean;
  children: React.ReactNode;
  enableTracking?: boolean;
  overrideSeatingService?: SeatingService;
  overrideStageService?: StageService;
  disableBestAvailableSeating?: boolean;
};

/**
 * Context provider for seatingService and stageService
 *
 * overrideSeatingService and overrideStageService are instances passed in from test files.
 * They are needed to spy on class values/functions. Otherwise, new class instances are created which are not accessible to tests.
 */
const SeatingStateProvider = ({
  basketType,
  isOnSeatingMapPage,
  children,
  enableTracking = true,
  overrideSeatingService = undefined, // value from test file
  overrideStageService = undefined, // value from test file
  disableBestAvailableSeating = true,
}: SeatingStateProviderProps): React.ReactElement => {
  const {
    addItems,
    modifyItems,
    removeItems,
    itemGroups,
    modifyItemGroups,
    paymentType,
    purchaseSourcePlatform,
  } = useBasketService();

  // Use a ref to track the round-robin index for renewal updates.
  // This pointer is used to determine which renewal item group to update next.
  const renewalUpdateIndexRef = useRef(0);

  // if the basket api is accessed when adding/removing items, keep the value true
  // else set the value to false (example: group sales map)
  const [requiresBasket, setRequiresBasket] = useState(true);

  // prevents the same modal from opening twice
  // ex: if we're on the map page, we don't want both the cart's removal modal and the map's removal modal to open
  const [isModalOutsideMap, setIsModalOutsideMap] = useState(false);

  // displays a loading spinner on the map page when seat selections are updated
  const [isUpdatingMap, setIsUpdatingMap] = useState(false);

  // displays a loading spinner on the map page when the map is loaded or reloaded
  const [isLoadingMap, setIsLoadingMap] = useState(true);

  // used to show loader when basket is updating and callbacks have yet to be ran
  // used for best available seating
  const [isSeatingBasketUpdating, setIsSeatingBasketUpdating] = useState(false);

  // used for the "X items selected" text on the map's cart
  const [countOfSelectedItems, setCountOfSelectedItems] = useState<number>(0);

  const [dynamicHoldSlug, setDynamicHoldSlug] = useState<string | undefined>(
    undefined
  );

  const [seatingObjectArray, setSeatingObjectArray] = useState<
    Array<SeatWithLocationDataType>
  >([]);

  const {
    bestAvailableSeatingQueryParams,
    updateBestAvailableSeatingQueryParams,
  } = useBestAvailableQueryParams();

  const [seatingServiceState, setSeatingServiceState] =
    useState<SeatingService>(
      () => overrideSeatingService ?? new SeatingService()
    );

  const [stageServiceState, setStageServiceState] = useState<StageService>(
    () => overrideStageService ?? new StageService(seatingServiceState)
  );

  const [seatingEventData, setSeatingEventData] =
    useState<SeatingEventPropertyMap>();
  const [seatingItemArray, setSeatingItemArray] =
    useState<Array<SeatingItemPropertyMap>>();
  const [packageItemGroupParent, setPackageItemGroupParent] = useState<
    number | null
  >(null);

  function updateSeatingEventData(input: SeatingEventPropertyMap) {
    if (isMembership(input)) {
      updateBestAvailableSeatingQueryParams({
        type: BestAvailableQueryActions.MEMBERSHIP_GROUP,
        payload: { membership_group: input.id },
      });
      if (input.membership_levels.length === 1) {
        updateBestAvailableSeatingQueryParams({
          type: BestAvailableQueryActions.MEMBERSHIP_LEVELS,
          payload: {
            membership_levels: {
              [input.membership_levels[0].id]: 1,
            },
          },
        });
      }
    } else {
      const ticketTypes = input?.ticket_types || input?.tickettype_set;
      updateBestAvailableSeatingQueryParams({
        type: BestAvailableQueryActions.EVENT,
        payload: { event: input.id },
      });
      if (ticketTypes?.length === 1 && !ticketTypes[0].is_password_protected) {
        updateBestAvailableSeatingQueryParams({
          type: BestAvailableQueryActions.TICKET_TYPES,
          payload: {
            ticket_types: {
              [ticketTypes[0].id]: 2,
            },
          },
        });
      }
    }
    setSeatingEventData(input);
  }

  function updateSeatingBasketUpdating(isBasketUpdating: boolean) {
    setIsSeatingBasketUpdating(isBasketUpdating);
  }

  function updateSeatingItemArray(input: Array<SeatingItemPropertyMap>) {
    setSeatingItemArray(input);
  }
  function updateItemGroupParent(input: number) {
    setPackageItemGroupParent(input);
  }

  function updateSeatingObjectArray() {
    setSeatingObjectArray(seatingServiceState.getSeatingObjectArray());
  }

  function synchronizeSeating() {
    updateSeatingObjectArray();
  }

  function resetSeating() {
    seatingServiceState.clearSeatingObjectArray();
  }

  function updateRequiresBasket(input: boolean) {
    setRequiresBasket(input);
  }
  function updateDynamicHoldSlug(input: string) {
    setDynamicHoldSlug(input);
  }

  function setBasketHoldID(id: number | undefined) {
    seatingServiceState.basketHoldID = id;
  }

  /**
   * Call this function after a map element is selected or when the service states are updated
   *
   * Updates the number of selected items shown on the seating cart
   * Updates state in the seatingService and stageService
   */
  function updateSeatingState() {
    // update the number of selected items
    setCountOfSelectedItems(
      seatingServiceState.cartPermissionsCount +
        seatingServiceState.pendingCartPermissionsCount +
        getTotalWaitlistedSeatQty(
          basketType,
          seatingServiceState.mapItemType,
          seatingEventData
        )
    );

    // update current service states
    setSeatingServiceState(seatingServiceState);
    setStageServiceState(stageServiceState);
  }

  const prevItemGroupsRef = useRef(itemGroups);
  useEffect(() => {
    if (prevItemGroupsRef.current === itemGroups) return;
    prevItemGroupsRef.current = itemGroups;

    if (!seatingServiceState.cartPermissions.length) return;

    const basketPermissionIds = new Set<number>();
    itemGroups.forEach((group) => {
      group.tt_seat_permissions?.forEach((id) => basketPermissionIds.add(id));
      group.tt_location_permissions?.forEach((id) =>
        basketPermissionIds.add(id)
      );
    });

    const stalePermissions = seatingServiceState.cartPermissions.filter(
      (cp) => !basketPermissionIds.has(cp.permissionId)
    );

    if (stalePermissions.length === 0) return;

    const isMapMounted =
      typeof stageServiceState.layers?.seatsAvailable?.findOne === "function";

    stalePermissions.forEach((cp) => {
      seatingServiceState.removeFromCartPermissions(cp.permissionId);
      if (isMapMounted) {
        stageServiceState.setLocationSelected(cp, false);
      }
    });

    updateSeatingState();
  }, [itemGroups]);

  // Variables for modal that removes seats with minimum purchase limits
  const {
    status: statusModal,
    execute: executeModal,
    resolve: resolveModal,
    reject: rejectModal,
    reset: resetModal,
  } = useAwaitComponent();
  // Show modal to remove minimum purchase limit seats while awaiting user input
  const modalOpen = statusModal === "awaiting";

  async function awaitModal(): Promise<SeatSelectionModalReturnType> {
    try {
      return await executeModal();
    } finally {
      resetModal();
    }
  }

  const [seatsToAdd, setSeatsToAdd] = useState<{
    originalSeat: SeatWithLocationDataType;
    originalPendingItem?: number;
    seatIdArray: Array<number>;
    modalType: Omit<SeatingModal, "minpurchase">;
    payWhatYouCanPrice?: string | null;
  }>();

  const [seatsToRemove, setSeatsToRemove] = useState<{
    originalSeat: SeatWithLocationDataType;
    seatIdArray: Array<number>;
    formattedSeatArray?: Array<FormattedSeatType>;
    modalType: SeatingModal;
  }>();

  const [removeAllSeatsData, setRemoveAllSeatsData] = useState<{
    $data: TicketItemGroupPermissionTypeSeat;
    eventData: SeatingEventPropertyMap;
  }>();

  const { seatsToMultiSelect, onMultiSeatSelect } = useMultiSeatSelect({
    cartPermissions: seatingServiceState.cartPermissions,
    getSeatInCartPermissions: seatingServiceState.getSeatInCartPermissions,
    clearSelections: stageServiceState.stage.clearSelections,
    seatingItemArray,
    setIsUpdatingMap,
    updateSeatingState,
    requestAddSeatPermissions: seatingServiceState.isModifySeatSelectionMap()
      ? requestModifySeatPermissions
      : requestAddSeatPermissions,
    requestRemoveSeatPermissions,
    awaitModal,
    stageServiceState,
    synchronizeSeating,
    disableBestAvailableSeating,
  });

  /**
   * Opens the modal to remove isolated seats from the cart
   * Displays a loading spinner while the cart updates
   * Returns null when the modal is canceled
   */
  async function handleRemoveIsolatedSeats(
    originalSeat: FormattedSeatType,
    isolatedSeats: Array<number>
  ): Promise<SeatSelectionModalReturnType | null> {
    setSeatsToRemove({
      originalSeat: originalSeat.$data,
      seatIdArray: isolatedSeats,
      modalType: SeatingModal.Isolated,
    });
    // open the isolated removal modal
    // wait for the modal to be dismissed
    const isolatedSeatSelections = await awaitModal();

    // display a spinner while isolated seats are being removed
    // don't display the spinner when the pending cart items are being updated
    // dont display the spinner when best available selection is being updated
    if (
      seatingServiceState.pendingCartPermissionsCount === 0 &&
      seatingServiceState.seatingObjectArray.length === 0
    ) {
      setIsUpdatingMap(true);
    }

    if (isolatedSeatSelections === null) {
      // return null when the modal is cancelled
      setIsUpdatingMap(false);
      return null;
    }

    // return selected seat
    return isolatedSeatSelections;
  }

  /**
   * Opens the modal to add isolated seats to the cart
   * Returns null when the modal is canceled
   */
  async function handleAddIsolatedSeats(
    originalSeat: FormattedSeatType,
    isolatedSeats: Array<number>
  ): Promise<SeatSelectionModalReturnType> {
    setSeatsToAdd({
      originalSeat: originalSeat.$data,
      originalPendingItem: originalSeat.minPurchaseLimit
        ? Number(originalSeat.itemTypeId)
        : undefined,
      seatIdArray: isolatedSeats,
      modalType: SeatingModal.Isolated,
      payWhatYouCanPrice: originalSeat.payWhatYouCanPrice,
    });
    // open the isolated selection modal
    // wait for the modal to be dismissed
    const isolatedSeatSelections = await awaitModal();

    // return selected seat
    return isolatedSeatSelections;
  }

  /**
   * Handles opening the isolated seat confirmation modals
   * Opens the add or remove modal for isolated seats depending on the value of the "remove" param
   * Returns null when the modal is canceled
   * Resets the values of isolatedSeatsToAdd and isolatedSeatsToRemove
   */
  async function getIsolatedSeatSelections({
    originalSeat,
    remove,
    seatDataWithLocation,
  }: {
    /* eslint-disable  */
    // the check for if originalSeat.data is undefined causes an eslint error saying the prop types are not used. disabled eslint as the error is wrong.
    originalSeat: FormattedSeatType;
    remove: boolean;
    seatDataWithLocation?: SeatWithLocationDataType;
    /* eslint-enable */
  }): Promise<SeatSelectionModalReturnType | null> {
    if (!originalSeat?.$data) {
      return null;
    }
    /**
     * Creates a seatLinkedList for a given row of seats
     * Used to check for isolated seats in situations where the seatLinkedList doesn't exist
     *
     * Example: Large seating maps only create seatLinkedList data for sections that are clicked into.
     * If the page is refreshed and seats are removed from the cart without clicking into the parent section, the seatLinkedList must be recreated.
     */

    if (
      !seatingServiceState?.seatLinkedList[originalSeat.$data.id] &&
      seatDataWithLocation?.locationData?.type ===
        enums.SEATING_LOCATION_TYPE_MAP.TYPE_ROW.id &&
      !seatingServiceState.seatingObjectArray.some(
        (seat) => seat.id === originalSeat.$data.id
      )
    ) {
      seatingServiceState.createSeatLinkedListForRow(seatDataWithLocation);
    }

    // Returns the isolated seats as an array of ID values
    // Example: [1, 2]
    const isolatedSeats = seatingServiceState.getIsolatedSeats({
      curr: originalSeat.$data.id,
      initialAvailable: remove ? 1 : 0,
    }) as Array<number>;

    // if purchase limit doesn't allow user to purchase the isolated seat, ignore isolated seats
    if (
      !isolatedSeats ||
      (typeof isolatedSeats === "object" &&
        seatingServiceState.overridePurchaseLimits?.max &&
        isolatedSeats.length > seatingServiceState.overridePurchaseLimits?.max)
    ) {
      return [];
    }

    try {
      if (remove) {
        // return isolated seats to remove
        return await handleRemoveIsolatedSeats(originalSeat, isolatedSeats);
      }
      // return isolated seats to add
      return await handleAddIsolatedSeats(originalSeat, isolatedSeats);
    } catch (error) {
      // catch in this case is when the modal is canceled.
      return null;
    } finally {
      // reset modal values
      setSeatsToAdd(undefined);
      setSeatsToRemove(undefined);
    }
  }

  /**
   * Opens the modal to add resale seats to the cart
   * Returns null when the modal is canceled
   */
  async function handleAddResaleSeats(
    originalSeat: FormattedSeatType,
    resaleId: number
  ): Promise<SeatSelectionModalReturnType> {
    const seatIdArray: Array<number> = seatingServiceState?.resaleMap[resaleId];

    setSeatsToAdd({
      originalSeat: originalSeat.$data,
      seatIdArray,
      modalType: SeatingModal.Resale,
    });
    // wait for the modal to be dismissed
    const resaleSeatSelections = await awaitModal();

    // return location's seats
    return resaleSeatSelections;
  }

  /**
   * Opens the modal to remove resale seats from the cart
   * Returns null when the modal is canceled
   */
  async function handleRemoveResaleSeats(
    originalSeat: FormattedSeatType,
    resaleId: number
  ): Promise<SeatSelectionModalReturnType> {
    // get the seat ids to remove from the cartPermissions array
    // use cartPermissions because if the page is refreshed, resaleMap won't have data for selected seats
    const seatIdArray: Array<number> = seatingServiceState?.cartPermissions
      .filter((perm) => perm.resaleId === resaleId && perm.seat)
      .map((filteredPerm) => Number(filteredPerm?.seat?.id));

    setSeatsToRemove({
      originalSeat: originalSeat.$data,
      seatIdArray,
      modalType: SeatingModal.Resale,
    });
    // wait for the modal to be dismissed
    const resaleSeatSelections = await awaitModal();

    // display a spinner while resale seats are being removed
    // don't display the spinner when the pending cart items are being updated
    if (seatingServiceState.pendingCartPermissionsCount === 0) {
      setIsUpdatingMap(true);
    }

    // return empty array
    return resaleSeatSelections;
  }

  /**
   * Handles opening the resale seat confirmation modals
   * Opens the add or remove modal for resale seats depending on the value of the "remove" param
   * Returns null when the modal is canceled
   * Resets the values of resaleSeatsToAdd and resaleSeatsToRemove
   */
  async function getResaleSeatSelections({
    originalSeat,
    resaleId,
    remove = false,
  }: {
    originalSeat: FormattedSeatType;
    resaleId: number;
    remove?: boolean;
  }) {
    try {
      if (remove) {
        // return full purchase seats to remove
        return await handleRemoveResaleSeats(originalSeat, resaleId);
      }
      // return full purchase seats to add
      return await handleAddResaleSeats(originalSeat, resaleId);
    } catch (error) {
      // catch in this case is when the modal is canceled.
      return null;
    } finally {
      // reset modal values
      setSeatsToAdd(undefined);
      setSeatsToRemove(undefined);
    }
  }

  /**
   * Opens the modal to add isolated full-purchase-required seats to the cart
   * Returns null when the modal is canceled
   */
  async function handleAddFullPurchaseSeats(
    $data: SeatWithLocationDataType
  ): Promise<SeatSelectionModalReturnType> {
    setSeatsToAdd({
      originalSeat: $data,
      seatIdArray:
        $data.locationData?.seats
          .filter((s) => s.id !== $data.id)
          .map((s) => s.id) ?? [],
      modalType: SeatingModal.FullPurchase,
    });
    // wait for the modal to be dismissed
    const FPRSeatSelections = await awaitModal();

    // return location's seats
    return FPRSeatSelections;
  }

  /**
   * Opens the modal to remove isolated full-purchase-required seats from the cart
   * Returns null when the modal is canceled
   */
  async function handleRemoveFullPurchaseSeats(
    $data: SeatWithLocationDataType
  ): Promise<SeatSelectionModalReturnType> {
    setSeatsToRemove({
      originalSeat: $data,
      seatIdArray:
        $data.locationData?.seats
          .filter((s) => s.id !== $data.id)
          .map((s) => s.id) ?? [],
      modalType: SeatingModal.FullPurchase,
    });
    // wait for the modal to be dismissed
    const FPRSeatSelections = await awaitModal();

    // display a spinner while full purchase seats are being removed
    // don't display the spinner when the pending cart items are being updated
    // don't display the spinner when best available selection is being updated
    if (
      seatingServiceState.pendingCartPermissionsCount === 0 &&
      seatingServiceState.seatingObjectArray.length === 0
    ) {
      setIsUpdatingMap(true);
    }

    // return empty array
    return FPRSeatSelections;
  }

  /**
   * Handles opening the full-purchase-required seat confirmation modals
   * Opens the add or remove modal for full purchase seats depending on the value of the "remove" param
   * Returns null when the modal is canceled
   * Resets the values of FPRSeatsToAdd and FPRSeatsToRemove
   */
  async function getFullPurchaseRequiredConfirmation(
    $data: SeatWithLocationDataType,
    remove = false
  ) {
    try {
      if (remove) {
        // return full purchase seats to remove
        return await handleRemoveFullPurchaseSeats($data);
      }
      // return full purchase seats to add
      return await handleAddFullPurchaseSeats($data);
    } catch (error) {
      // catch in this case is when the modal is canceled.
      return null;
    } finally {
      // reset modal values
      setSeatsToAdd(undefined);
      setSeatsToRemove(undefined);
    }
  }

  /**
   * Updates existing location permissions for an item group
   * This method specifically handles modifying location permissions already attached to an item group
   */
  async function requestUpdateLocationPermissions(
    locationArray: Array<{
      itemTypeId: number;
      permissionId: number;
      qty: number;
      payWhatYouCanPrice?: string;
    }>,
    location?: LocationDataType,
    itemGroupId?: number
  ): Promise<void> {
    setIsUpdatingMap(true);
    if (!seatingServiceState.isProcessing()) {
      seatingServiceState.setProcessing(true);
      try {
        if (requiresBasket) {
          // Get the current basket state
          const refreshedBasket = useStore.getState().getBasket(basketType);

          // Find the target item group if provided
          let targetGroup;
          if (itemGroupId) {
            targetGroup = refreshedBasket?.item_groups?.find(
              (group) => group.id === itemGroupId
            );

            if (!targetGroup) {
              throw new Error(`Item group with ID ${itemGroupId} not found`);
            }
          }

          if (targetGroup) {
            // This is a modification to an existing item group
            const locationData = locationArray[0];

            // Collect existing location permission IDs to remove
            const oldLocationPermissionIds =
              targetGroup.tt_location_permissions || [];

            // Collect existing seat permission IDs to remove
            const oldSeatPermissionIds = targetGroup.tt_seat_permissions || [];

            // Find old permissions in cart to update UI
            const oldLocationPermissions = oldLocationPermissionIds
              .map((permId) =>
                seatingServiceState.cartPermissions.find(
                  (p) => p.permissionId === permId
                )
              )
              .filter(Boolean);

            // Find old seat permissions in cart to update UI
            const oldSeats = oldSeatPermissionIds.map(
              (permId) =>
                seatingServiceState.cartPermissions.find(
                  (p) => p.permissionId === permId
                )?.seat as SeatWithLocationDataType
            );

            // Check if all groups that are NOT being modified already have permissions.
            const eachItemGroupHasPermissions = checkItemGroupsHavePermissions(
              refreshedBasket?.item_groups ?? [],
              targetGroup.id
            );

            // Update the item group with the new location permission
            await modifyItemGroups(
              [
                {
                  itemGroupId: targetGroup.id,
                  fieldsToUpdate: {
                    tt_seat_permissions: [],
                    permissions: [],
                    tt_location_permissions: [locationData.permissionId],
                    location_permissions: [
                      {
                        id: locationArray?.[0]?.permissionId,
                        location: {
                          count: locationArray?.[0]?.qty,
                          id: location?.id,
                          identifier: location?.identifier,
                          segment_identifier: location?.section,
                        },
                      },
                    ],
                    membership_level: locationData.itemTypeId,
                    quantity: locationData.qty,
                  },
                },
              ],
              {
                shouldPersist: eachItemGroupHasPermissions,
              }
            );

            // Remove old seat permissions from client-side state
            oldSeatPermissionIds.forEach((permissionId) => {
              seatingServiceState.removeFromCartPermissions(permissionId);
            });

            // Deselect the old seats on the seating map for UI consistency.
            oldSeats.forEach((oldSeat) => {
              if (oldSeat) {
                stageServiceState.setLocationSelected(
                  { seat: oldSeat as SeatWithLocationDataType },
                  false
                );
              }
            });

            // Remove old permissions from client-side state
            oldLocationPermissions.forEach((oldPerm) => {
              if (
                oldPerm &&
                oldPerm.permissionId !== locationData.permissionId
              ) {
                // Checks if other items are selected on the same location
                // Deselects location only when all items from the same location have been removed
                const hasOtherItemsInCart = Boolean(
                  seatingServiceState.cartPermissions.find(
                    (p) =>
                      p?.location?.id === oldPerm?.location?.id &&
                      p.permissionId !== oldPerm?.permissionId &&
                      // This additional check ensures we don't count permissions we're about to remove
                      !oldLocationPermissions.some(
                        (op) =>
                          op?.permissionId === p.permissionId &&
                          op?.permissionId !== locationData.permissionId
                      )
                  )
                );
                seatingServiceState.removeFromCartPermissions(
                  oldPerm.permissionId
                );

                if (oldPerm.location) {
                  stageServiceState.setLocationSelected(
                    { location: oldPerm.location },
                    hasOtherItemsInCart
                  );
                }
              }
            });

            // Add new permission to client-side state if not already there
            if (
              location &&
              !seatingServiceState.getCartPermission(locationData.permissionId)
            ) {
              const cartPermissionLocation = {
                id: location.id,
                count: locationData.qty,
                identifier: location.identifier,
                section: location.section,
                itemTypeId: locationData.itemTypeId,
              };

              seatingServiceState.addToCartPermissions({
                permissionId: locationData.permissionId,
                itemTypeId: locationData.itemTypeId,
                location: cartPermissionLocation,
              });

              stageServiceState.setLocationSelected(
                {
                  location: cartPermissionLocation,
                },
                true
              );
            } else if (location) {
              // Update the quantity of an existing permission
              const existingPerm = seatingServiceState.getCartPermission(
                locationData.permissionId
              );
              if (existingPerm && existingPerm.location) {
                existingPerm.location.count = locationData.qty;
              }
            }
          }
        }
      } finally {
        setIsUpdatingMap(false);
        seatingServiceState.setProcessing(false);
      }
    }
  }

  /**
   * Combines seat and location permissions into a single basket update.
   * This is more efficient than making separate calls for seats and locations.
   */
  async function requestAddSeatsAndLocationsToCart({
    seats,
    locations,
    itemGroupParent,
    isWaitlist = false,
    withModify = false,
  }: {
    seats?: Array<FormattedSeatType>;
    locations?: Array<{
      itemTypeId: number;
      permissionId: number;
      qty: number;
      location?: {
        id: number;
        identifier: string;
        section?: string;
        count?: number;
      };
      payWhatYouCanPrice?: string;
    }>;
    itemGroupParent?: number;
    isWaitlist?: boolean;
    withModify?: boolean;
  }): Promise<void> {
    if (StageService.isAndroidDevice()) {
      setIsUpdatingMap(true);
    }

    if (!seatingServiceState.isProcessing()) {
      seatingServiceState.setProcessing(true);
      try {
        if (requiresBasket) {
          const refreshedBasket = useStore.getState().getBasket(basketType);

          // Prepare seats for basket update
          const seatsToAdd =
            seats?.map((seat) => {
              const { payWhatYouCanPrice, ...seatWithoutPwycPrice } = seat;
              return {
                item: {
                  ...seatWithoutPwycPrice,
                  mapItemType: seatingServiceState.mapItemType,
                  accepted_terms: basketType === DataProvider.Venue,
                },
                quantity: 1,
                parent: itemGroupParent ?? null,
                pay_what_you_can_price: payWhatYouCanPrice,
              };
            }) ?? [];

          // Prepare locations for basket update
          const locationsToAdd =
            locations?.map((loc) => {
              const { payWhatYouCanPrice, ...locWithoutPwycPrice } = loc;
              return {
                item: {
                  ...locWithoutPwycPrice,
                  mapItemType: seatingServiceState.mapItemType,
                  itemGroupParent,
                  accepted_terms: basketType === DataProvider.Venue,
                },
                quantity: loc.qty,
                parent: itemGroupParent ?? null,
                pay_what_you_can_price: payWhatYouCanPrice,
              };
            }) ?? [];

          if (withModify) {
            await modifyItems([...seatsToAdd, ...locationsToAdd], {
              ...refreshedBasket,
              hold_slug: dynamicHoldSlug,
              is_waitlist: isWaitlist,
            });
          } else {
            // Combine both updates into a single basket update
            await addItems([...seatsToAdd, ...locationsToAdd], {
              ...refreshedBasket,
              hold_slug: dynamicHoldSlug,
              is_waitlist: isWaitlist,
            });
          }
        }

        // Update UI state for seats
        seats?.forEach((seat) => {
          const { permissionId, $data, itemTypeId, resaleId } = seat;
          seatingServiceState.addToCartPermissions({
            permissionId,
            resaleId,
            itemTypeId,
            seat: { ...$data, itemTypeId },
          });
          stageServiceState.setLocationSelected({ seat: $data }, true);

          seatingServiceState.deleteSeatPermissions(seat);
        });

        // Update UI state for locations
        locations?.forEach((loc) => {
          if (loc.qty > 0 && loc.location) {
            seatingServiceState.addToCartPermissions({
              permissionId: loc.permissionId,
              itemTypeId: loc.itemTypeId,
              location: {
                id: loc.location.id,
                count: loc.qty,
                identifier: loc.location.identifier,
                section: loc.location.section,
                itemTypeId: loc.itemTypeId,
              },
            });
            stageServiceState.setLocationSelected(
              { location: { ...loc.location, count: loc.qty } },
              true
            );
          }
        });
      } catch (error) {
        // Revert UI state for seats on error
        seats?.forEach((seat) => {
          const { $data } = seat;
          stageServiceState.setLocationSelected({ seat: $data }, false);
        });

        // Revert UI state for locations on error
        locations?.forEach((loc) => {
          if (loc.location) {
            stageServiceState.setLocationSelected(
              { location: { ...loc.location, count: loc.qty } },
              false
            );
          }
        });

        throw error;
      } finally {
        setIsUpdatingMap(false);
        seatingServiceState.setProcessing(false);
      }
    }
  }

  /**
   * Attempt to add a location permission to the cart.
   */
  async function requestModifyLocationPermissions(
    locationArray: Array<{
      itemTypeId: number;
      permissionId: number;
      qty: number;
      payWhatYouCanPrice?: string;
    }>,
    location?: LocationDataType,
    itemGroupParent?: number,
    isWaitlist = false
  ): Promise<void> {
    // Convert to the new combined function format
    return requestAddSeatsAndLocationsToCart({
      locations: locationArray.map((loc) => ({
        ...loc,
        location: location
          ? {
              id: location.id,
              identifier: location.identifier,
              section: location.section || "",
              count: location.count || 0,
            }
          : undefined,
      })),
      itemGroupParent,
      isWaitlist,
      withModify: true,
    });
  }

  // Helper function to handle seat permissions requests
  async function requestSeatPermissions(
    seats: Array<FormattedSeatType>,
    operation: (seatsToAdd: SeatsToAddType[]) => Promise<void>
  ) {
    if (StageService.isAndroidDevice()) {
      setIsUpdatingMap(true);
    }
    if (!seatingServiceState.isProcessing()) {
      seatingServiceState.setProcessing(true);
      try {
        if (requiresBasket) {
          const seatsToAdd = seats.map((seat) => {
            const { payWhatYouCanPrice, ...seatWithoutPwycPrice } = seat;

            return {
              item: {
                ...seatWithoutPwycPrice,
                mapItemType: seatingServiceState.mapItemType,
                accepted_terms: basketType === DataProvider.Venue, // accept terms by default on the box office
              },
              quantity: 1,
              parent: seat.itemGroupParent,
              pay_what_you_can_price: payWhatYouCanPrice,
            };
          });

          await operation(seatsToAdd);
        }

        seats.forEach((seat) => {
          const { permissionId, $data, itemTypeId, resaleId } = seat;
          seatingServiceState.addToCartPermissions({
            permissionId,
            resaleId,
            itemTypeId,
            seat: { ...$data, itemTypeId },
          });
          stageServiceState.setLocationSelected({ seat: $data }, true);

          seatingServiceState.deleteSeatPermissions(seat);
        });
      } catch (error) {
        // undo selected styling on the map if seats can't be added to the cart
        seats.forEach((seat) => {
          const { $data } = seat;
          stageServiceState.setLocationSelected({ seat: $data }, false);
        });

        // propagate error
        throw error;
      } finally {
        setIsUpdatingMap(false);
        seatingServiceState.setProcessing(false);
      }
    }
  }

  /**
   * Attempt to modify a seat permission to the cart if cartContext is provided.
   * Otherwise, modify the seat to cartPermissions without adding to the cart. (group sales)
   */
  async function requestModifySeatPermissions(
    seats: Array<FormattedSeatType>,
    itemGroupId?: number
  ) {
    const operation = async (seatsToAdd: SeatsToAddType[]) => {
      // Get all renewal item groups from the basket.
      const renewalGroups = itemGroups.filter((group) => group.renewal_member);

      if (seatsToAdd.length > renewalGroups.length) {
        throw new Error(
          `You can only select up to ${renewalGroups.length} seats for renewals.`
        );
      }

      let oldPermissionIds: number[] = [];
      let oldSeats: Array<SeatWithLocationDataType> = [];
      let oldLocationPermissionIds: number[] = [];

      // If itemGroupId is provided, find the specific group to update
      let groupsToUpdate;
      if (itemGroupId) {
        // Find the specific group by ID
        const targetGroup = renewalGroups.find(
          (group) => group.id === itemGroupId
        );
        if (!targetGroup) {
          throw new Error(`Renewal group with ID ${itemGroupId} not found`);
        }
        groupsToUpdate = [targetGroup];
      } else {
        // Build a subset of renewal groups starting at an offset determined by the ref.
        // This ensures the groups to update will depend on the ref and will wrap around if needed.
        groupsToUpdate = Array.from({ length: seatsToAdd.length }, (_, idx) => {
          // Use the ref offset and wrap if we go past the end of renewalGroups
          const groupIndex =
            (renewalUpdateIndexRef.current + idx) % renewalGroups.length;
          return renewalGroups[groupIndex];
        });
      }

      // Map the selected groups to modifications using the corresponding seats from seatsToAdd.
      // The seat index is calculated relative to the same ref and wrapped using modulo.
      const modifications = groupsToUpdate.map((group, idx) => {
        // Collect old permissions from group (if available)
        if (group.tt_seat_permissions && group.tt_seat_permissions.length > 0) {
          oldPermissionIds = oldPermissionIds.concat(group.tt_seat_permissions);
        }

        if (
          group.tt_location_permissions &&
          group.tt_location_permissions.length > 0
        ) {
          oldLocationPermissionIds = oldLocationPermissionIds.concat(
            group.tt_location_permissions
          );
        }

        // Collect old seat info for UI deselection, assuming group.$data holds current seat info.
        oldSeats = oldPermissionIds.map((permissionId) => {
          return seatingServiceState.cartPermissions.find(
            (p) => p.permissionId === permissionId
          )?.seat as SeatWithLocationDataType;
        });
        const seatItem = seatsToAdd[idx].item;

        return {
          itemGroupId: group.id,
          fieldsToUpdate: {
            tt_location_permissions: [],
            location_permissions: [],
            tt_seat_permissions: [seatItem.permissionId],
            permissions: [
              {
                id: seatItem.permissionId,
                resale_submission_id: null,
                seat: {
                  id: seatItem?.$data?.id,
                  identifier: seatItem?.$data?.identifier,
                  is_accessible: seatItem?.$data?.is_accessible,
                  is_limited_visibility: seatItem?.$data?.is_limited_visibility,
                  location_full_purchase_required:
                    seatItem?.$data?.location_full_purchase_required,
                  location_identifier: seatItem?.$data?.location_identifier,
                  segment_identifier: seatItem?.$data?.segment_identifier,
                },
              },
            ],
            membership_level: seatItem.itemTypeId,
            quantity: 1,
          },
        };
      });

      // Check if all groups that are NOT being modified already have permissions.
      // Groups being modified will have permissions after this call.
      const groupIdsBeingModified = new Set<number | undefined>(
        groupsToUpdate.map((group) => group.id)
      );
      const eachItemGroupHasPermissions = checkItemGroupsHavePermissions(
        renewalGroups,
        groupIdsBeingModified
      );

      // If all unmodified groups have permissions, and we're adding permissions to modified groups,
      // then all groups will have permissions after this modification.
      await modifyItemGroups(modifications, {
        shouldPersist: eachItemGroupHasPermissions,
      });

      // Remove each of the old seat permissions from the client-side cart.
      oldPermissionIds.forEach((permissionId) => {
        seatingServiceState.removeFromCartPermissions(permissionId);
      });

      oldLocationPermissionIds.forEach((permissionId) => {
        const locationPerm =
          seatingServiceState.getCartPermission(permissionId);
        seatingServiceState.removeFromCartPermissions(permissionId);

        // If this permission has a location, deselect it on the map
        if (locationPerm?.location) {
          stageServiceState.setLocationSelected(
            { location: locationPerm.location },
            false
          );
        }
      });

      // Deselect the old seats on the seating map for UI consistency.
      oldSeats.forEach((oldSeat) => {
        if (oldSeat) {
          stageServiceState.setLocationSelected(
            { seat: oldSeat as SeatWithLocationDataType },
            false
          );
        }
      });

      // If itemGroupId is not provided, update the ref after mapping so that it points to the next seat.
      if (!itemGroupId) {
        renewalUpdateIndexRef.current =
          (renewalUpdateIndexRef.current + seatsToAdd.length) %
          renewalGroups.length;
      }
    };
    return requestSeatPermissions(seats, operation);
  }

  /**
   * Attempt to add a seat permission to the cart if cartContext is provided.
   * Otherwise, add the seat to cartPermissions without adding to the cart. (group sales)
   */
  async function requestAddSeatPermissions(seats: Array<FormattedSeatType>) {
    const operation = async (seatsToAdd: SeatsToAddType[]) => {
      const refreshedBasket = useStore.getState().getBasket(basketType);

      // For non-renewal cases, simply add the seats to the basket normally.
      await addItems(seatsToAdd, {
        ...refreshedBasket,
        hold_slug: dynamicHoldSlug,
      });
    };
    return requestSeatPermissions(seats, operation);
  }

  /**
   * Removes a seat from the cart in cases where we have seat data.
   * Otherwise, remove the seat from cartPermissions without removing from the cart. (group sales)
   * example: from clicking the selected seat node on the map
   */
  async function requestRemoveSeatPermissions(
    seats: Array<FormattedSeatType>
  ): Promise<void> {
    if (StageService.isAndroidDevice()) {
      setIsUpdatingMap(true);
    }
    if (!seatingServiceState.isProcessing()) {
      seatingServiceState.setProcessing(true);
      try {
        if (requiresBasket) {
          const refreshedBasket = useStore.getState().getBasket(basketType);

          const permissionIds = seats.map((seat) => seat.permissionId);
          const itemGroupsWithPermission =
            refreshedBasket?.item_groups?.filter((group) =>
              group.tt_seat_permissions?.some((p) => permissionIds.includes(p))
            ) ?? [];

          const renewalGroups = itemGroupsWithPermission.filter(
            (group) => group.renewal_member
          );
          const nonRenewalGroups = itemGroupsWithPermission.filter(
            (group) => !group.renewal_member
          );

          // For renewal groups: clear seat permissions locally without persisting.
          // The persist will happen when all groups have permissions again (on next seat add).
          // This aligns with the add flow which uses shouldPersist: eachItemGroupHasPermissions.
          if (renewalGroups.length > 0) {
            const modifications = renewalGroups.map((group) => ({
              itemGroupId: group.id,
              fieldsToUpdate: {
                tt_seat_permissions: [],
                tt_location_permissions: [],
                permissions: [],
                location_permissions: [],
              },
            }));

            await modifyItemGroups(modifications, {
              shouldPersist: false,
            });
          }

          // Remove non-renewal groups normally
          if (nonRenewalGroups.length > 0) {
            await removeItems(
              nonRenewalGroups,
              {
                ...refreshedBasket,
                hold_slug: dynamicHoldSlug,
              },
              permissionIds
            );
          }
        }

        /**
         * Group sales assigned seating does not have a cart context, so in order to remove selections
         * from the group sales assigned seating map
         */
        seats.forEach((seat) => {
          const { permissionId, $data } = seat;
          seatingServiceState.removeFromCartPermissions(permissionId);
          stageServiceState.setLocationSelected({ seat: $data }, false);
        });
      } finally {
        // hide spinner after isolated or FPR seats have been removed
        setIsUpdatingMap(false);
        seatingServiceState.setProcessing(false);
      }
    }
  }

  /**
   * Opens a modal that removes all seats with a specified minimum purchase item type from the basket
   * All seats of this item type must be removed to prevent situations where the basket has less than the minimum amount for that item type
   * Example: scenarios like removing isolated seats and/or full-purchase seats, where multiple items are removed at once
   */
  async function getMinPurchaseSeatSelections({
    seat,
    inCartPermission,
    minPurchaseLimit,
    itemGroupParent,
  }: {
    seat: FormattedSeatType;
    inCartPermission: CartPermissionType;
    minPurchaseLimit?: number;
    itemGroupParent?: number;
  }): Promise<SeatSelectionModalReturnType | null> {
    try {
      let seatsWithMinPurchaseLimits: Array<CartPermissionType> = [];

      // remove all seats in the cart when multiple item types contribute to the purchase limit
      // this prevents the number of items falling below the minimum limit
      if (seatingServiceState.overridePurchaseLimits?.multiItemsAllowed) {
        seatsWithMinPurchaseLimits = seatingServiceState.cartPermissions.filter(
          ({ location }) => !location
        );
      }
      // else remove all seats of a certain item type (items with a minimum purchase limit)
      else {
        seatsWithMinPurchaseLimits =
          seatingServiceState.filterCartSeatsByProperty(
            "itemTypeId",
            inCartPermission?.itemTypeId
          );
      }

      const formattedSeatsToRemove = seatsWithMinPurchaseLimits.map((s) =>
        SeatingService.formatSeatForCart({
          itemTypeId: s.itemTypeId,
          permissionId: s.permissionId,
          resaleId: s.resaleId,
          itemGroupParent,
          $data: s.seat as SeatWithLocationDataType,
          minPurchaseLimit,
        })
      );

      // set the data used in the modal
      setSeatsToRemove({
        originalSeat: seat.$data,
        seatIdArray: [],
        formattedSeatArray: formattedSeatsToRemove,
        modalType: SeatingModal.MinPurchase,
      });

      // prompt the user that the seats related to the minimum purchase limit will be removed
      const minSeatsToRemove = await awaitModal();

      // display a spinner on the map while minimum purchase limit seats are being removed
      setIsUpdatingMap(true);

      return minSeatsToRemove;
    } catch (error) {
      // catch in this case is when the modal is canceled.
      return null;
    } finally {
      // reset modal values
      setSeatsToRemove(undefined);
    }
  }

  /**
   * Gets seats in the pending cart permissions array and removes them
   * Updates the map styling by deselecting the removed seats
   */
  function removePendingCartPermissions(seats: Array<FormattedSeatType>) {
    // get the seats to remove from the pending cart array
    const seatsToRemove = seats.filter(
      (seat) => !seatingServiceState.getCartPermission(seat.permissionId)
    );

    // deselect the removed seats on the map
    seatsToRemove.map((seat) =>
      stageServiceState.setLocationSelected({ seat: seat.$data }, false)
    );

    // get the seats to keep in the pending cart array
    const seatsToKeep = seatingServiceState.pendingCartPermissions.filter(
      (seat: FormattedSeatType) =>
        !seatsToRemove.some((s) => s.$data.id === seat.$data.id)
    );

    // update the pending cart seat array and item count
    seatingServiceState.pendingCartPermissions = seatsToKeep;

    // if all pending seats are removed, exit the limited seat selection mode
    if (seatingServiceState.pendingCartPermissionsCount === 0) {
      stageServiceState.isLimitedSeatSelectionEnabled = false;
    }

    updateSeatingState();
  }

  /**
   * Attempt to remove seat permission from cart
   * Used on interactions where we don't have seat data
   * example: from clicking the delete icon on the map page's cart
   */
  async function removeSeatPermission(
    permissionId: number,
    itemGroupParentId: number | null,
    eventData?: SeatingEventPropertyMap
  ): Promise<void | null> {
    // start processing request
    if (seatingServiceState && !seatingServiceState.isProcessing()) {
      // check if seat has a permission in cart
      const inCartPermission =
        seatingServiceState.getCartPermission(permissionId);

      // check if seat has a pending permission
      const inPendingCartPermission =
        seatingServiceState.getPendingCartPermission(permissionId);

      // remove the seat from the cart or the pending array
      const returnedPermission = inCartPermission ?? inPendingCartPermission;

      let minPurchaseLimit;

      // override the item-level minimum purchase limit
      // example: minimum limit defined on a dynamic hold
      if (seatingServiceState.overridePurchaseLimits?.min) {
        minPurchaseLimit = seatingServiceState.overridePurchaseLimits?.min;
      }
      // else use the minimum purchase limit defined on the item type
      else if (returnedPermission && returnedPermission.itemTypeId) {
        minPurchaseLimit =
          seatingServiceState.itemTypeMap[returnedPermission.itemTypeId]
            ?.minimum_purchase_limit;
      }

      const hasMinPurchaseSeatsInCart: boolean =
        Boolean(minPurchaseLimit) && Boolean(inCartPermission);

      const seat: FormattedSeatType = SeatingService.formatSeatForCart({
        itemTypeId: returnedPermission?.itemTypeId,
        permissionId: returnedPermission?.permissionId as number,
        $data: returnedPermission?.seat as SeatWithLocationDataType,
        itemGroupParent: itemGroupParentId,
        resaleId: returnedPermission?.resaleId,
        minPurchaseLimit,
      });

      const hasResaleItem = Boolean(seat.resaleId);

      let seats: Array<FormattedSeatType> = [seat];

      // Get seat data with location data
      const seatDataWithLocation = await stageServiceState.getSeatData(
        seat.$data.id,
        seat.$data
      );

      if (hasResaleItem && seat.resaleId) {
        const seatsToRemove = await getResaleSeatSelections({
          originalSeat: seat,
          resaleId: seat.resaleId,
          remove: true,
        });

        // return null when the modal is cancelled and hide loading spinner
        if (seatsToRemove === null) {
          setIsUpdatingMap(false);
          return null;
        }
        seats = seatsToRemove;
      }

      // if min purchase items are in the actual cart, remove all of them at once
      // this prevents issues with the number of minimum purchase seats still in the cart during isolated/full-purchase seat removal
      if (minPurchaseLimit && inCartPermission && !hasResaleItem) {
        const seatsToRemove = await getMinPurchaseSeatSelections({
          seat,
          inCartPermission,
          minPurchaseLimit,
          itemGroupParent: itemGroupParentId ?? undefined,
        });

        // return null when the modal is cancelled and hide loading spinner
        if (seatsToRemove === null) {
          setIsUpdatingMap(false);
          return null;
        }
        seats = seats.concat(seatsToRemove);
      }

      // If the event doesn't allow isolated seats, display a prompt to confirm removal
      if (
        eventData?.no_isolated_seats &&
        !hasMinPurchaseSeatsInCart &&
        !hasResaleItem
      ) {
        const isolatedSeatSelections = await getIsolatedSeatSelections({
          originalSeat: seat,
          seatDataWithLocation,
          remove: true,
        });

        // return null when the modal is cancelled and hide loading spinner
        if (isolatedSeatSelections === null) {
          setIsUpdatingMap(false);
          return null;
        }
        seats = seats.concat(isolatedSeatSelections);
      }

      // If the seat is part of a location that requires a full purchase, display a prompt to confirm removal
      if (
        seatDataWithLocation?.locationData?.full_purchase_required &&
        seatDataWithLocation?.locationData.seats?.length > 1 &&
        !hasMinPurchaseSeatsInCart &&
        !hasResaleItem
      ) {
        const fullPurchaseRequiredSeats =
          await getFullPurchaseRequiredConfirmation(seatDataWithLocation, true);

        // return null when the modal is cancelled and hide loading spinner
        if (fullPurchaseRequiredSeats === null) {
          setIsUpdatingMap(false);
          return null;
        }
        seats = seats.concat(fullPurchaseRequiredSeats);
      }

      // remove seats from the pending array
      if (minPurchaseLimit && inPendingCartPermission) {
        removePendingCartPermissions(seats);
        return null;
      }

      // display a spinner while seat is being removed
      setIsUpdatingMap(true);

      // resets permissions in seatingMap when seats are removed from the cart
      seatingServiceState.resetMultipleSeatPermissions(seats);

      try {
        await requestRemoveSeatPermissions(seats);
      } catch (error) {
        // if there is an error, delete the permissions that were reset
        seatingServiceState.deleteMultipleSeatPermissions(seats);
        throw error;
      }
    }
    return null;
  }

  async function $removeAllSeatsConfirmation(
    permission: TicketItemGroupPermissionType,
    permissions: Array<TicketItemGroupPermissionType | TicketItemGroupType>,
    eventData: SeatingEventPropertyMap
  ): Promise<void> {
    const refreshedBasket = useStore.getState().getBasket(basketType);
    const itemGroupsToUse = refreshedBasket?.item_groups ?? [];

    try {
      setRemoveAllSeatsData({
        $data: permission.seat as TicketItemGroupPermissionTypeSeat,
        eventData,
      });

      const removeAllItems = await awaitModal();

      // remove all items if user continues
      if (removeAllItems) {
        const permissionIds = permissions.map((p) => Number(p.id));
        const itemGroupToRemove = itemGroupsToUse.filter((group) =>
          group.tt_seat_permissions?.some((p) => permissionIds.includes(p))
        );

        await removeItems(
          itemGroupToRemove,
          {
            hold_slug: dynamicHoldSlug,
          },
          permissionIds
        );
      }
    } catch (error) {
      // propagate the error if non-null
      if (error instanceof Error && error !== null) {
        throw new Error(error.message);
      }
    }
  }

  /**
   * Called from components outside of the map to remove seat permissions
   * example: function is called from the mobile cart
   */
  async function initRemoveSeatPermissions(
    permission: TicketItemGroupPermissionType,
    itemGroup: TicketItemGroupType,
    itemGroupSeatingObject: SeatingEventPropertyMap
  ): Promise<void> {
    setIsModalOutsideMap(true);

    const refreshedBasket = useStore.getState().getBasket(basketType);
    const itemGroupsToUse = refreshedBasket?.item_groups ?? [];

    // check if an event has no_isolated_seats active
    const noIsolatedSeats = itemGroupSeatingObject.no_isolated_seats;

    // check if a seat is part of a location that has full_purchase_required
    const fullPurchaseRequiredSeat =
      permission?.seat?.location_full_purchase_required;

    /**
     * Combines item groups permissions
     */
    const combinePermissions = (
      acc: Array<TicketItemGroupPermissionType>,
      curr: TicketItemGroupType
    ) => {
      if (
        (curr.event === itemGroupSeatingObject.id ||
          curr.membership_group === itemGroupSeatingObject.id) && // has same event or group id
        curr.permissions && // contains permissions
        curr.permissions.find((p) => p.seat) // make sure that permission has a seat
      ) {
        if (fullPurchaseRequiredSeat) {
          return acc.concat(
            curr.permissions.filter(
              (p) =>
                p.seat &&
                permission.seat &&
                p.seat.location_full_purchase_required &&
                p.seat.location_identifier ===
                  permission.seat.location_identifier // find permissions with the same seat location
            )
          );
        }

        return acc.concat(curr.permissions);
      }

      return acc;
    };

    // Determine if this action needs user confirmation
    const needsConfirmation = fullPurchaseRequiredSeat || noIsolatedSeats;

    // check if multiple seats from the same event by calculating combined permissions
    const combinedPermissions = itemGroupsToUse
      ? itemGroupsToUse?.reduce(combinePermissions, [])
      : [];

    // only check for items on the currently-displayed map
    const isOnEventMap =
      itemGroup.event === seatingEventData?.id &&
      seatingServiceState.mapItemType === common.SeatingMapItemType.EVENT;
    const isOnMembershipMap =
      itemGroup.membership_group === seatingEventData?.id &&
      seatingServiceState.mapItemType === common.SeatingMapItemType.MEMBERSHIP;
    const isSeatingMapVisible =
      isOnSeatingMapPage && (isOnEventMap || isOnMembershipMap);

    if (needsConfirmation && combinedPermissions.length > 0) {
      // check if you're on an assigned seating event page
      // and the item you're removing belongs to that event
      if (isSeatingMapVisible) {
        // check for isolated seats and full purchase required seats
        await removeSeatPermission(
          permission.id,
          itemGroup.parent,
          itemGroupSeatingObject
        );
        updateSeatingState();
      } else {
        // if you're on a seating page for another event or a non-seating page,
        // prompt user to remove all seats or navigate to event to change seats
        await $removeAllSeatsConfirmation(
          permission,
          combinedPermissions,
          itemGroupSeatingObject
        );
      }
    } else {
      setIsUpdatingMap(true);

      // remove seats from the cart if there are no isolated or full purchase seats
      const itemGroupToRemove = itemGroupsToUse.filter((group) =>
        group.tt_seat_permissions?.includes(permission.id)
      );

      await removeItems(
        itemGroupToRemove,
        {
          hold_slug: dynamicHoldSlug,
        },
        [permission.id]
      );

      if (isSeatingMapVisible) {
        // update map appearance if the user is on the correct seating page
        seatingServiceState.removeFromCartPermissions(permission.id);
        stageServiceState.setLocationSelected(permission, false);
        updateSeatingState();
      }
      setIsUpdatingMap(false);
    }
    setIsModalOutsideMap(false);
  }

  /**
   * Called from components outside of the map to modify (add OR remove) location permissions.
   * Pass in a quantity of 0 to fully remove the item.
   * example: used on the mobile cart
   */
  async function initModifyLocationPermissions(
    permission: TicketItemGroupPermissionType,
    itemGroup: TicketItemGroupType,
    quantity: number
  ): Promise<void> {
    // get the current item_groups
    const refreshedBasket = useStore.getState().getBasket(basketType);
    const itemGroupsToUse = refreshedBasket?.item_groups ?? [];
    const isWaitlist = Boolean(refreshedBasket?.is_waitlist);

    // only check for items on the currently-displayed map
    const isOnEventMap =
      itemGroup.event === seatingEventData?.id &&
      seatingServiceState.mapItemType === common.SeatingMapItemType.EVENT;
    const isOnMembershipMap =
      itemGroup.membership_group === seatingEventData?.id &&
      seatingServiceState.mapItemType === common.SeatingMapItemType.MEMBERSHIP;
    const isSeatingMapVisible =
      isOnSeatingMapPage && (isOnEventMap || isOnMembershipMap);

    // check if on an assigned seating event page and the item you're modifying belongs to that event
    if (isSeatingMapVisible) {
      const itemId: number = itemGroup.type ?? itemGroup.membership_level;

      // modify the location items in the cart and update the map display
      await requestModifyLocationPermissions(
        [
          {
            itemTypeId: itemId,
            permissionId: permission.id,
            qty: quantity,
          },
        ],
        undefined,
        packageItemGroupParent,
        isWaitlist
      );
      updateSeatingState();
    } else {
      const itemGroupToModify = itemGroupsToUse.find((group) =>
        group.tt_location_permissions?.includes(permission.id)
      );

      // modify the location items without needing to update the map
      await modifyItems(
        {
          item: {
            ...itemGroupToModify,
            mapItemType: seatingServiceState.mapItemType,
          },
          quantity,
        },
        {
          ...refreshedBasket,
          is_waitlist: isWaitlist,
        }
      );
    }
  }

  // Filters all itemGroups with location permissions
  // Returns the length of this filtered array or the fallback number
  // Used for itemGroups with location permissions AND info_required = true
  function getInfoRequiredLocationQtyInCart(
    itemId: number,
    permissionId: number,
    fallbackNumber: number
  ): number {
    // get the current item_groups
    const refreshedBasket = useStore.getState().getBasket(basketType);
    const itemGroupsToUse = refreshedBasket?.item_groups ?? [];

    return Number(
      itemGroupsToUse?.reduce((total: number, ig: TicketItemGroupType) => {
        let newTotal = total;
        if (
          (ig.membership_level === itemId || ig.type === itemId) &&
          ig.tt_location_permissions &&
          ig.tt_location_permissions.length > 0 &&
          ig.tt_location_permissions.includes(permissionId)
        ) {
          const locationQty =
            ig.location_permissions?.find((p) => p.id === permissionId)
              ?.location?.count ?? 0;
          newTotal += locationQty;
        }
        return newTotal;
      }, 0) ?? fallbackNumber
    );
  }

  // Finds an itemGroup with location permissions
  // Returns itemGroup.quantity or the fallback number
  // Used for itemGroups with location permissions AND info_required = false
  function getLocationQtyInCart(
    itemId: number,
    permissionId: number,
    fallbackNumber: number
  ) {
    // get the current item_groups
    const refreshedBasket = useStore.getState().getBasket(basketType);
    const itemGroupsToUse = refreshedBasket?.item_groups ?? [];

    return Number(
      itemGroupsToUse?.find(
        (ig: TicketItemGroupType) =>
          (ig.membership_level === itemId || ig.type === itemId) &&
          ig.tt_location_permissions &&
          ig.tt_location_permissions.length > 0 &&
          ig.tt_location_permissions.includes(permissionId)
      )?.quantity ?? fallbackNumber
    );
  }

  // De-selects all seats/locations, which resets the map appearance
  // Use case: run this function on cart expiry
  function resetMapSelections() {
    if (seatingServiceState.cartPermissions.length > 0) {
      seatingServiceState.cartPermissions.forEach(
        (permission: CartPermissionType) => {
          stageServiceState.setLocationSelected(permission, false);
        }
      );
      seatingServiceState.cartPermissions = [];
      updateSeatingState();
    }
  }

  const hasSeatsWithMultipleTypes = useMemo(() => {
    return (
      Object.values(seatingServiceState.seatingMap).some(
        (ticketTypes) => ticketTypes.length > 1
      ) ||
      Object.values(seatingServiceState.locationMap).some(
        (locationTypes) => locationTypes.length > 1
      )
    );
  }, [seatingServiceState.seatingMap, seatingServiceState.locationMap]);

  const hasPasswordProtectedSeats = useMemo(() => {
    return (
      Object.values(seatingServiceState.seatingMap).some((ticketTypes) =>
        ticketTypes.some(
          (ticketType) => ticketType.seatingItemPasswordProtected
        )
      ) ||
      Object.values(seatingServiceState.locationMap).some((locationTypes) =>
        locationTypes.some(
          (locationType) => locationType.seatingItemPasswordProtected
        )
      )
    );
  }, [seatingServiceState.seatingMap, seatingServiceState.locationMap]);

  // goes through the seating map and returns array of unique item types that have been added to the map
  // seatingItemArray contains types with the right visibility, which can include types that are not on the map.
  const typesOnMap = useMemo(() => {
    const typesOnMap = new Set<number>();
    Object.values(seatingServiceState.seatingMap).forEach((ticketTypes) => {
      ticketTypes.forEach((ticketType) => {
        typesOnMap.add(ticketType.seatingItemTypeId);
      });
    });
    Object.values(seatingServiceState.locationMap).forEach((locationTypes) => {
      locationTypes.forEach((locationType) => {
        typesOnMap.add(locationType.seatingItemTypeId);
      });
    });
    return Array.from(typesOnMap);
  }, [seatingServiceState.seatingMap, seatingServiceState.locationMap]);

  useEffect(() => {
    if (!hasSeatsWithMultipleTypes && !isLoadingMap) {
      updateBestAvailableSeatingQueryParams({
        type: BestAvailableQueryActions.QUANTITY,
        payload: { quantity: 2 },
      });
    }
  }, [hasSeatsWithMultipleTypes, isLoadingMap]);

  //
  const canShowBestAvailableSeating = useMemo(() => {
    if (isLoadingMap) {
      return false;
    }
    return (
      (hasSeatsWithMultipleTypes &&
        Boolean(bestAvailableSeatingQueryParams.ticket_types)) ||
      !hasSeatsWithMultipleTypes
    );
  }, [
    hasSeatsWithMultipleTypes,
    bestAvailableSeatingQueryParams.ticket_types,
    isLoadingMap,
  ]);

  const value = {
    basketType,
    paymentType,
    purchaseSourcePlatform,
    enableTracking,
    stageServiceState,
    seatingServiceState,
    dynamicHoldSlug,
    seatingEventData,
    seatingItemArray,
    packageItemGroupParent,
    seatingObjectArray,
    // Computed property - derive selected seat IDs from seating object array
    selectedSeatIds: seatingObjectArray.map((seat) => seat.id),
    updateSeatingObjectArray,
    requiresBasket,
    updateSeatingEventData,
    updateSeatingItemArray,
    updateItemGroupParent,
    updateSeatingState,
    synchronizeSeating,
    resetSeating,
    updateDynamicHoldSlug,
    isUpdatingMap,
    isLoadingMap,
    isOnSeatingMapPage,
    setIsLoadingMap,
    setIsUpdatingMap,
    updateRequiresBasket,
    isModalOutsideMap,
    countOfSelectedItems,
    setDynamicHoldSlug,
    setBasketHoldID,
    getInfoRequiredLocationQtyInCart,
    getLocationQtyInCart,
    resetMapSelections,
    bestAvailableSeatingQueryParams,
    updateBestAvailableSeatingQueryParams,
    disableBestAvailableSeating,
    hasSeatsWithMultipleTypes,
    hasPasswordProtectedSeats,
    canShowBestAvailableSeating,
    typesOnMap,
    isSeatingBasketUpdating,
    updateSeatingBasketUpdating,
    modalValues: {
      seatsToAdd,
      seatsToRemove,
      seatsToMultiSelect,
      removeAllSeatsData,
      modalOpen,
      awaitModal,
      resolveModal,
      rejectModal,
    },
    actions: {
      removePendingCartPermissions,
      getIsolatedSeatSelections,
      getFullPurchaseRequiredConfirmation,
      getMinPurchaseSeatSelections,
      getResaleSeatSelections,
      initRemoveSeatPermissions,
      initModifyLocationPermissions,
      requestRemoveSeatPermissions,
      removeSeatPermission,
      requestModifySeatPermissions,
      requestAddSeatPermissions,
      requestModifyLocationPermissions,
      requestUpdateLocationPermissions,
      onMultiSeatSelect,
      handleRemoveIsolatedSeats, // exported for tests
      handleAddIsolatedSeats, // exported for tests
      handleAddFullPurchaseSeats, // exported for tests
      handleRemoveFullPurchaseSeats, // exported for tests
      requestAddSeatsAndLocationsToCart,
    },
  };

  return (
    <SeatingStateContext.Provider value={value}>
      {children}
    </SeatingStateContext.Provider>
  );
};

const useSeatingState = (): SeatingStateContextType => {
  const context = useContext(SeatingStateContext);
  if (context === undefined) {
    throw new Error(
      "useSeatingState must be used within a SeatingStateProvider"
    );
  }
  return context;
};

export { SeatingStateProvider, useSeatingState };
